/**
 * Offline/sync status store
 *
 * Manages:
 * - Online/offline status
 * - Offline queue state
 * - Sync status and errors
 * - Queue persistence
 */

import { defineStore } from 'pinia'
import { ref, computed, onScopeDispose } from 'vue'
import { onlineManager } from '@tanstack/vue-query'

export interface QueuedMutation {
  id: string
  type: string
  timestamp: number
  payload: Record<string, unknown>
  retry: number
  lastError?: string
}

export const useOfflineStore = defineStore('offline', () => {
  const isOnline = ref(navigator.onLine)
  const queue = ref<QueuedMutation[]>([])
  const isSyncing = ref(false)
  const syncError = ref<string | null>(null)

  const hasQueue = computed(() => queue.value.length > 0)
  const queueLength = computed(() => queue.value.length)

  const addToQueue = (mutation: Omit<QueuedMutation, 'id' | 'timestamp' | 'retry'>) => {
    const queueItem: QueuedMutation = {
      ...mutation,
      id: crypto.randomUUID(),
      timestamp: Date.now(),
      retry: 0,
    }
    queue.value.push(queueItem)
    persistQueue()
  }

  const removeFromQueue = (id: string) => {
    queue.value = queue.value.filter((item) => item.id !== id)
    persistQueue()
  }

  const clearQueue = () => {
    queue.value = []
    persistQueue()
  }

  const setOnline = (online: boolean) => {
    isOnline.value = online
    onlineManager.setOnline(online)
    if (online) {
      syncError.value = null
    }
  }

  // A suspended page can miss the online event. Re-read the browser hint on
  // return and resume paused queries as well as the shared connection display.
  const refreshOnline = () => {
    const online = navigator.onLine
    if (online !== isOnline.value) setOnline(online)
    else onlineManager.setOnline(online)
  }
  const becameOnline = () => setOnline(true)
  const becameOffline = () => setOnline(false)
  const visibilityChanged = () => {
    if (document.visibilityState === 'visible') refreshOnline()
  }

  const setSyncing = (syncing: boolean) => {
    isSyncing.value = syncing
  }

  const setSyncError = (error: string | null) => {
    syncError.value = error
  }

  const persistQueue = () => {
    localStorage.setItem('offlineQueue', JSON.stringify(queue.value))
  }

  const loadQueue = () => {
    const stored = localStorage.getItem('offlineQueue')
    if (stored) {
      queue.value = JSON.parse(stored)
    }
  }

  // Initialize from storage
  loadQueue()

  // Listen to online/offline events
  window.addEventListener('online', becameOnline)
  window.addEventListener('offline', becameOffline)
  window.addEventListener('focus', refreshOnline)
  window.addEventListener('pageshow', refreshOnline)
  document.addEventListener('visibilitychange', visibilityChanged)
  onScopeDispose(() => {
    window.removeEventListener('online', becameOnline)
    window.removeEventListener('offline', becameOffline)
    window.removeEventListener('focus', refreshOnline)
    window.removeEventListener('pageshow', refreshOnline)
    document.removeEventListener('visibilitychange', visibilityChanged)
  })

  return {
    isOnline,
    queue,
    isSyncing,
    syncError,
    hasQueue,
    queueLength,
    addToQueue,
    removeFromQueue,
    clearQueue,
    setOnline,
    refreshOnline,
    setSyncing,
    setSyncError,
    loadQueue,
  }
})
